import { Box, Flex, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { FormNumberInput } from 'src/components/molecules/formInput/FormNumberInput';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { ForecastFormInputs } from '.';
import { useCurrency } from 'src/hooks/useCurrency';

type Media = {
  id: string;
  name: string;
};

type ScenarioBuilderProps = {
  media: Media[];
};

export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({ media }) => {
  const { control } = useFormContext<ForecastFormInputs>();
  const { symbol: currencySymbol } = useCurrency();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'scenarios',
  });

  const addScenario = () => {
    const usedNums = new Set(
      fields
        .map((f) => {
          const match = /^Scenario (\d+)$/.exec(f.scenarioName ?? '');
          return match ? Number(match[1]) : null;
        })
        .filter((n): n is number => n !== null),
    );
    let nextNum = 1;
    while (usedNums.has(nextNum)) nextNum++;
    append({
      scenarioName: `Scenario ${nextNum}`,
      mediaSpend: media.map((m) => ({ channelId: m.id, spend: 0 })),
    });
  };

  return (
    <Box mb={{ base: '12px', md: '24px' }}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text
          fontSize="md"
          fontWeight="semibold"
          fontFamily="marketingPage"
          color="content.title">
          Scenarios
        </Text>
        <Button
          variant="outline"
          buttonName="add-scenario"
          size="sm"
          onClick={addScenario}>
          <Icon iconName="plus" boxSize={3} mr={1} />
          Add Scenario
        </Button>
      </Flex>

      {fields.map((field, scenarioIndex) => (
        <Box
          key={field.id}
          borderWidth="1px"
          borderRadius="md"
          borderColor="surface.border"
          p={4}
          mb={4}>
          <Flex justifyContent="space-between" alignItems="flex-start" mb={4}>
            <Box flex={1}>
              <FormTextInput
                inputPath={`scenarios.${scenarioIndex}.scenarioName`}
                placeholder="Scenario name"
                label="Scenario name"
                required
              />
            </Box>
            {fields.length > 1 && (
              <Button
                buttonName="remove-scenario"
                variant="ghost"
                size="sm"
                ml={2}
                mt={6}
                onClick={() => remove(scenarioIndex)}>
                Remove
              </Button>
            )}
          </Flex>

          <Text
            fontSize="sm"
            fontWeight="medium"
            fontFamily="marketingPage"
            color="content.muted"
            mb={3}>
            Planned spend per period
          </Text>
          {media.map((m, mediaIndex) => (
            <Flex key={m.id} alignItems="center" gap={3} mb={2}>
              <Text
                fontSize="sm"
                fontFamily="marketingPage"
                color="content.title"
                minW="200px"
                noOfLines={1}>
                {m.name}
              </Text>
              <Box maxW="200px">
                <FormNumberInput
                  inputPath={`scenarios.${scenarioIndex}.mediaSpend.${mediaIndex}.spend`}
                  placeholder="0"
                  suffix={currencySymbol}
                />
              </Box>
            </Flex>
          ))}
        </Box>
      ))}
    </Box>
  );
};
